import React, { useState, useEffect, useRef } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSearch, faTimes, faFilter, faChevronDown, faSpinner } from '@fortawesome/free-solid-svg-icons';
import { crmService } from '../services/crmService';
import axiosInstance from '../utils/axiosConfig';
import { CATEGORIES, getSubtypesForCategory } from '../data/productTaxonomy';

const StockItemsSearch = ({
    onSearchResults,
    onFiltersChange,
    isSearching,
    onSearchStateChange,
    onClearSearch,
    activeCategory,
    activeSubtype,
}) => {
    const [query, setQuery] = useState('');
    const [filters, setFilters] = useState({
        category: activeCategory || '',
        product_type: activeSubtype || '',
        country: '',
        supplier: '',
        source_location: ''
    });
    const [showFilters, setShowFilters] = useState(false);

    const [countries, setCountries] = useState([]);
    const [suppliers, setSuppliers] = useState([]);
    const [loadingData, setLoadingData] = useState(true);

    const [showSupplierDropdown, setShowSupplierDropdown] = useState(false);
    const [supplierSearchTerm, setSupplierSearchTerm] = useState('');
    const [filteredSuppliers, setFilteredSuppliers] = useState([]);
    const [highlightIndex, setHighlightIndex] = useState(-1);
    const supplierDropdownRef = useRef(null);
    const supplierInputRef = useRef(null);

    const subtypeOptionsForFilter = filters.category
        ? getSubtypesForCategory(filters.category)
        : [];

    useEffect(() => {
        setFilters((prev) => ({
            ...prev,
            category: activeCategory || '',
            product_type: activeSubtype || '',
        }));
    }, [activeCategory, activeSubtype]);

    // Load initial data
    useEffect(() => {
        const loadData = async () => {
            try {
                setLoadingData(true);
                
                // Load countries and suppliers in parallel
                const [countriesResponse, suppliersData] = await Promise.all([
                    axiosInstance.get('/api/stock/countries/'),
                    crmService.getPartnerData()
                ]);
                
                setCountries(countriesResponse.data || []);
                setSuppliers(suppliersData.sort((a, b) => a.name.localeCompare(b.name)));
                
                console.log(`Loaded ${countriesResponse.data.length} countries and ${suppliersData.length} suppliers for stock search`);
            } catch (error) {
                console.error('Error loading search data:', error);
            } finally {
                setLoadingData(false);
            }
        };

        loadData();
    }, []);

    // Handle outside clicks for supplier dropdown
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (supplierDropdownRef.current && !supplierDropdownRef.current.contains(event.target)) {
                setShowSupplierDropdown(false);
                setSupplierSearchTerm('');
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    // Filter suppliers based on search term
    useEffect(() => {
        if (!supplierSearchTerm.trim()) {
            setFilteredSuppliers([]);
        } else {
            const filtered = suppliers.filter(supplier =>
                supplier.name.toLowerCase().includes(supplierSearchTerm.toLowerCase())
            );
            setFilteredSuppliers(filtered.slice(0, 10)); // Limit to 10 results
        }
    }, [supplierSearchTerm, suppliers]);

    // Reset keyboard highlight whenever the visible results change
    useEffect(() => {
        setHighlightIndex(-1);
    }, [filteredSuppliers, showSupplierDropdown]);

    // Debounced search - only for text query, not filters
    useEffect(() => {
        const timer = setTimeout(() => {
            // Only auto-search for text queries, not filters
            if (query.length >= 2) {
                performSearch();
            } else if (query.length === 0) {
                // EMPTY SEARCHBAR = SHOW ALL ITEMS
                console.log('🔥 SEARCHBAR IS EMPTY - CLEARING SEARCH AND SHOWING ALL ITEMS');
                onSearchStateChange(false);
                onSearchResults([]);
                onFiltersChange({});
                if (onClearSearch) {
                    console.log('🔥 CALLING onClearSearch()');
                    onClearSearch();
                }
            }
        }, 300);

        return () => clearTimeout(timer);
    }, [query]); // Removed filters from dependency array

    const performSearch = async () => {
        const hasQuery = query.length >= 2; // Minimum 2 characters
        const hasFilters = Object.values(filters).some(value => value !== '');
        
        console.log('🔍 performSearch called:', { hasQuery, hasFilters, activeFiltersCount: Object.values(filters).filter(Boolean).length });
        
        // If no query and no filters, notify parent that search is cleared
        if (!hasQuery && !hasFilters) {
            console.log('🔍 No query and no filters - clearing search');
            onSearchStateChange(false);
            onSearchResults([]);
            onFiltersChange({});
            // Signal to parent to exit search mode
            if (onClearSearch) {
                onClearSearch();
            }
            return;
        }

        onSearchStateChange(true);
        
        try {
            const params = new URLSearchParams();
            
            if (hasQuery) {
                params.append('q', query);
            }
            
            Object.entries(filters).forEach(([key, value]) => {
                if (value) {
                    params.append(key, value);
                }
            });

            const searchUrl = `/api/stock/search/?${params.toString()}`;
            console.log('🔍 Search request:', searchUrl);
            
            const response = await axiosInstance.get(searchUrl);
            console.log('🔍 Search results:', response.data.results?.length || 0, 'items found');
            
            onSearchResults(response.data.results || []);
            onFiltersChange(filters);
        } catch (error) {
            console.error('🔍 Search API error:', error);
            console.error('🔍 Error response:', error.response?.data);
            onSearchResults([]);
        } finally {
            // Only signal search is done, but don't exit search mode if we have results/filters
            onSearchStateChange(false);
            console.log('🔍 Search completed');
        }
    };

    const handleFilterChange = (filterName, value) => {
        console.log('🔧 Filter changed:', filterName, '=', value);
        setFilters(prev => {
            const newFilters = {
                ...prev,
                [filterName]: value
            };
            console.log('🔧 New filters state:', newFilters);
            return newFilters;
        });
    };

    const handleSupplierSelect = (supplier) => {
        handleFilterChange('supplier', supplier.name);
        setShowSupplierDropdown(false);
        setSupplierSearchTerm('');
    };

    const handleSupplierKeyDown = (e) => {
        if (e.key === 'Escape') {
            setShowSupplierDropdown(false);
            return;
        }
        if (!showSupplierDropdown || filteredSuppliers.length === 0) return;
        if (e.key === 'ArrowDown' || (e.key === 'Tab' && !e.shiftKey)) {
            e.preventDefault();
            setHighlightIndex(prev => Math.min(prev + 1, filteredSuppliers.length - 1));
        } else if (e.key === 'ArrowUp' || (e.key === 'Tab' && e.shiftKey)) {
            e.preventDefault();
            setHighlightIndex(prev => Math.max(prev - 1, 0));
        } else if (e.key === 'Enter' && highlightIndex >= 0) {
            e.preventDefault();
            handleSupplierSelect(filteredSuppliers[highlightIndex]);
        }
    };

    const handleCategoryFilterChange = (value) => {
        setFilters((prev) => {
            const stillValid = getSubtypesForCategory(value).some(
                (s) => s.value === prev.product_type,
            );
            return {
                ...prev,
                category: value,
                product_type: stillValid ? prev.product_type : '',
            };
        });
    };

    const clearFilters = () => {
        setFilters({
            category: '',
            product_type: '',
            country: '',
            supplier: '',
            source_location: ''
        });
        setQuery('');
    };

    const activeFiltersCount = Object.values(filters).filter(Boolean).length;

    return (
        <div className="stock-items-search">
            <div className="search-header">
                <div className="search-input-group">
                    <div className="search-input-container">
                        <FontAwesomeIcon 
                            icon={isSearching ? faSpinner : faSearch} 
                            className={`search-icon ${isSearching ? 'fa-spin' : ''}`}
                        />
                        <input
                            type="text"
                            placeholder="Search by Type, Country, Supplier, Source Location"
                            value={query}
                            onChange={(e) => setQuery(e.target.value)}
                            className="search-input compact"
                        />
                        {query && (
                            <button
                                className="clear-search"
                                onClick={() => setQuery('')}
                            >
                                <FontAwesomeIcon icon={faTimes} />
                            </button>
                        )}
                    </div>
                    
                    <button
                        className={`filter-toggle-button ${showFilters ? 'active' : ''}`}
                        onClick={() => setShowFilters(!showFilters)}
                    >
                        <FontAwesomeIcon icon={faFilter} />
                        Filters
                        {activeFiltersCount > 0 && (
                            <span className="filter-count">{activeFiltersCount}</span>
                        )}
                        <FontAwesomeIcon 
                            icon={faChevronDown} 
                            className={`chevron ${showFilters ? 'rotated' : ''}`}
                        />
                    </button>
                </div>
            </div>

            {showFilters && (
                <div className="search-filters-panel">
                    <div className="filters-grid">
                        {/* Category */}
                        <div className="form-group">
                            <label>Category</label>
                            <select
                                value={filters.category}
                                onChange={(e) => handleCategoryFilterChange(e.target.value)}
                                className="form-select"
                                disabled={loadingData}
                            >
                                <option value="">All Categories</option>
                                {CATEGORIES.map((cat) => (
                                    <option key={cat.value} value={cat.value}>
                                        {cat.label}
                                    </option>
                                ))}
                            </select>
                        </div>

                        {/* Subtype */}
                        <div className="form-group">
                            <label>Subtype</label>
                            <select
                                value={filters.product_type}
                                onChange={(e) => handleFilterChange('product_type', e.target.value)}
                                className="form-select"
                                disabled={loadingData || !filters.category}
                            >
                                <option value="">{filters.category ? 'All Subtypes' : 'Pick a category first'}</option>
                                {subtypeOptionsForFilter.map((sub) => (
                                    <option key={sub.value} value={sub.value}>
                                        {sub.label}
                                    </option>
                                ))}
                            </select>
                        </div>

                        {/* Country */}
                        <div className="form-group">
                            <label>Country</label>
                            <select
                                value={filters.country}
                                onChange={(e) => handleFilterChange('country', e.target.value)}
                                className="form-select"
                                disabled={loadingData}
                            >
                                <option value="">All Countries</option>
                                {countries.map(country => (
                                    <option key={country.value} value={country.value}>
                                        {country.label}
                                    </option>
                                ))}
                            </select>
                        </div>

                        {/* Supplier */}
                        <div className="form-group" ref={supplierDropdownRef}>
                            <label>Supplier</label>
                            <div className="supplier-select-container">
                                <div 
                                    className={`crm-modern-multi-select-header compact ${showSupplierDropdown ? 'active' : ''}`}
                                    onClick={() => {
                                        if (!loadingData) {
                                            setShowSupplierDropdown(!showSupplierDropdown);
                                            if (!showSupplierDropdown) {
                                                setTimeout(() => {
                                                    if (supplierInputRef.current) {
                                                        supplierInputRef.current.focus();
                                                    }
                                                }, 150);
                                            }
                                        }
                                    }}
                                >
                                    <span>
                                        {filters.supplier || 'All Suppliers'}
                                    </span>
                                    <FontAwesomeIcon icon={faChevronDown} className="dropdown-arrow" />
                                </div>
                                
                                {showSupplierDropdown && (
                                    <div className="crm-modern-multi-select-dropdown compact">
                                        <div className="crm-search-input-container">
                                            <input
                                                ref={supplierInputRef}
                                                type="text"
                                                className="crm-search-input"
                                                placeholder="Search suppliers..."
                                                value={supplierSearchTerm}
                                                onChange={(e) => setSupplierSearchTerm(e.target.value)}
                                                onKeyDown={handleSupplierKeyDown}
                                                autoFocus
                                            />
                                        </div>

                                        <div className="crm-modern-multi-select-options">
                                            <div 
                                                className="crm-modern-multi-select-option"
                                                onClick={() => {
                                                    handleFilterChange('supplier', '');
                                                    setShowSupplierDropdown(false);
                                                    setSupplierSearchTerm('');
                                                }}
                                                style={{ cursor: 'pointer' }}
                                            >
                                                <span>All Suppliers</span>
                                            </div>
                                            
                                            {!supplierSearchTerm.trim() ? (
                                                <div className="crm-modern-multi-select-option">
                                                    <span style={{ fontStyle: 'italic', color: '#666' }}>
                                                        Start typing to search suppliers...
                                                    </span>
                                                </div>
                                            ) : filteredSuppliers.length > 0 ? (
                                                filteredSuppliers.map((supplier, idx) => (
                                                    <div
                                                        key={supplier.id}
                                                        ref={idx === highlightIndex ? (el) => el?.scrollIntoView({ block: 'nearest' }) : null}
                                                        className={`crm-modern-multi-select-option ${idx === highlightIndex ? 'is-highlighted' : ''}`}
                                                        onClick={() => handleSupplierSelect(supplier)}
                                                        style={{ cursor: 'pointer' }}
                                                    >
                                                        <span>{supplier.name}</span>
                                                    </div>
                                                ))
                                            ) : (
                                                <div className="crm-modern-multi-select-option">
                                                    <span style={{ fontStyle: 'italic', color: '#666' }}>
                                                        No suppliers found matching "{supplierSearchTerm}"
                                                    </span>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>

                        {/* Source Location */}
                        <div className="form-group">
                            <label>Source Location</label>
                            <input
                                type="text"
                                value={filters.source_location}
                                onChange={(e) => handleFilterChange('source_location', e.target.value)}
                                className="form-input"
                                placeholder="Filter by source location..."
                                disabled={loadingData}
                            />
                        </div>
                    </div>
                    
                    <div className="filters-actions">
                        <button
                            className="btn btn-primary btn-sm"
                            onClick={() => {
                                console.log('🎯 Apply button clicked!');
                                console.log('🎯 Current filters state:', filters);
                                console.log('🎯 Current query state:', query);
                                console.log('🎯 Active filters count:', Object.values(filters).filter(Boolean).length);
                                performSearch();
                                setShowFilters(false); // Close filters when apply is pressed
                            }}
                            disabled={isSearching}
                        >
                            {isSearching ? 'Applying...' : 'Apply'}
                        </button>
                        <button
                            className="btn btn-secondary btn-sm"
                            onClick={clearFilters}
                            disabled={query === '' && activeFiltersCount === 0}
                        >
                            Clear All
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
};

export default StockItemsSearch; 